import { notifyError } from "@/lib/notify-error";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useBlocker, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, PageHeaderSkeleton, type Crumb } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { EditableDataGrid, type EditableDataGridHandle } from "@/components/editable-data-grid";
import { SpecPilotSourcePdfPanel } from "@/components/spec-pilot-source-pdf-panel";
import { UnsavedChangesDialog } from "@/components/unsaved-changes-dialog";
import { RevisionBadge } from "@/components/semantic-badges";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProject } from "@/components/projects/project-context";
import { useRouteParam } from "@/lib/use-route-param";
import { useAppNavigate, type AppNavigateOptions } from "@/lib/use-app-navigate";
import {
  getTableRowsPayload,
  type TableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";
import { rowPagesAfterEdit, uploadTableRowsPayload } from "@/lib/table-rows-upload";
import { ActionLogProvider } from "@/hooks/use-action-log-session";
import { useCanEdit } from "@/lib/capabilities";

// The `project_` escape keeps this page out of the `/project` layout route:
// ProjectRoute has a pre-existing render loop when it mounts as a layout for
// in-app navigations under /project/documents/*, and this editor needs none
// of its data. The URL is unchanged: /project/documents/:documentId/scheduleTables.
export const Route = createFileRoute("/project_/documents/$documentId_/scheduleTables")({
  component: ScheduleTablesEditorPage,
});

/**
 * Master schedule table editor. Edits the shared extraction tables directly,
 * so changes propagate to SpecPilot, TakeoffPilot, and everywhere else the
 * schedule extraction is consumed.
 */
export function ScheduleTablesEditorPage() {
  const { projectId, setProjectId } = useProject();
  const documentId = useRouteParam("documentId");
  const routeSearch = useSearch({ strict: false }) as Record<string, string | undefined>;
  const initialRevisionId = useRouteParam("revisionId") ?? routeSearch.revisionId ?? null;

  const { data: documentDetails, isPending: isDocumentPending } = useQuery(
    convexQuery(
      api.projectDocuments.getDocumentWithRevisions,
      documentId
        ? {
            documentId: documentId as Id<"projectDocuments">,
          }
        : "skip"
    )
  );

  useEffect(() => {
    if (!documentDetails) return;
    if (projectId === documentDetails.document.projectId) return;
    setProjectId(documentDetails.document.projectId);
  }, [documentDetails, projectId, setProjectId]);

  const activeRevision = useMemo(() => {
    const revisions = documentDetails?.revisions ?? [];
    if (revisions.length === 0) return null;
    if (initialRevisionId) {
      const requested = revisions.find((revision) => revision._id === initialRevisionId);
      if (requested) return requested;
    }
    const sorted = [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber);
    return (
      sorted.find((revision) => revision.sharedScheduleExtraction?.status === "completed") ??
      sorted.find((revision) => revision.latestSharedScheduleExtractionId) ??
      sorted[0] ??
      null
    );
  }, [documentDetails, initialRevisionId]);

  if (!documentId) {
    return (
      <div className="py-20 text-center text-muted-foreground">Invalid document route.</div>
    );
  }

  if (isDocumentPending) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <Card>
          <ProcessingSpinner message="Loading schedule tables..." />
        </Card>
      </div>
    );
  }

  if (!documentDetails || !activeRevision) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Document Not Found"
          trail={[{ label: "Project Documents", href: "/project/documents" }]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Document not found</AlertTitle>
          <AlertDescription>
            The document you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <ActionLogProvider
      projectId={documentDetails.document.projectId ?? null}
      pilot="DocPilot"
      targetKind="projectDocument"
      targetId={documentId}
    >
      <ScheduleTablesEditorContent
        documentId={documentId}
        documentName={documentDetails.document.name}
        revision={activeRevision}
      />
    </ActionLogProvider>
  );
}

type RevisionWithExtraction = NonNullable<
  FunctionReturnType<typeof api.projectDocuments.getDocumentWithRevisions>
>["revisions"][number];

function ScheduleTablesEditorContent({
  documentId,
  documentName,
  revision,
}: {
  documentId: string;
  documentName: string;
  revision: RevisionWithExtraction;
}) {
  const appNavigate = useAppNavigate();
  const extractionStatus = revision.sharedScheduleExtraction?.status ?? null;

  const { data: tables } = useQuery(
    convexQuery(
      api.projectDocumentScheduleTableEdits.listEditableTablesByRevision,
      extractionStatus === "completed" ? { revisionId: revision._id } : "skip"
    )
  );
  const { data: sourcePdfUrl } = useQuery(
    convexQuery(api.projectDocuments.getRevisionDownloadUrl, { revisionId: revision._id })
  );
  const sourcePdf = useMemo(
    () => ({
      fileUrl: sourcePdfUrl ?? null,
      filename: revision.originalFilename,
    }),
    [sourcePdfUrl, revision.originalFilename]
  );

  const updateMasterTableData = useMutation(
    api.projectDocumentScheduleTableEdits.updateMasterTableData
  );
  const discardUncommittedRowsPayload = useMutation(
    api.projectDocumentScheduleTableEdits.discardUncommittedRowsPayload
  );
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [pendingTableId, setPendingTableId] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<AppNavigateOptions | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const gridRef = useRef<EditableDataGridHandle>(null);
  const canEdit = useCanEdit();
  const pageTitle = canEdit ? "Edit Schedule Tables" : "Schedule Tables";
  const allowNextRouterNavigationRef = useRef(false);

  const selectedTable = useMemo(() => {
    if (!tables || tables.length === 0) return null;
    return tables.find((table) => String(table._id) === selectedTableId) ?? tables[0] ?? null;
  }, [tables, selectedTableId]);

  const rowsPayloadRef = selectedTable?.rowsPayloadRef ?? null;
  const rowsPayloadKey = rowsPayloadRef
    ? `${rowsPayloadRef.tableId}:${rowsPayloadRef.rowsVersion}`
    : null;
  const latestRowsPayloadRef = useRef(rowsPayloadRef);
  latestRowsPayloadRef.current = rowsPayloadRef;
  const lastHydratedRowsPayloadKey = useRef<string | null>(null);
  const editBaseRef = useRef<{ tableId: string; rowsVersion: number } | null>(null);
  const [rowPayload, setRowPayload] = useState<TableRowsPayload | null>(null);
  const [rowPayloadError, setRowPayloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const payloadRef = latestRowsPayloadRef.current;
    if (hasChanges) {
      return () => {
        cancelled = true;
      };
    }
    if (rowsPayloadKey && lastHydratedRowsPayloadKey.current === rowsPayloadKey) {
      return () => {
        cancelled = true;
      };
    }

    setRowPayload(null);
    setRowPayloadError(null);
    editBaseRef.current = null;
    if (!payloadRef || !rowsPayloadKey) {
      lastHydratedRowsPayloadKey.current = null;
      return () => {
        cancelled = true;
      };
    }
    const hydrationPayloadRef = payloadRef as TableRowsPayloadRef;

    async function hydrateRows() {
      try {
        const payload = await getTableRowsPayload(hydrationPayloadRef);
        if (!cancelled) {
          setRowPayload(payload);
          lastHydratedRowsPayloadKey.current = rowsPayloadKey;
          editBaseRef.current = {
            tableId: String(hydrationPayloadRef.tableId),
            rowsVersion: hydrationPayloadRef.rowsVersion,
          };
        }
      } catch (error) {
        console.error("Failed to load schedule table rows", error);
        if (!cancelled) {
          setRowPayload(null);
          setRowPayloadError("Failed to load table rows.");
        }
      }
    }

    void hydrateRows();
    return () => {
      cancelled = true;
    };
  }, [hasChanges, rowsPayloadKey]);

  const shouldBlockNavigation = useCallback(() => {
    if (allowNextRouterNavigationRef.current) {
      allowNextRouterNavigationRef.current = false;
      return false;
    }
    return hasChanges;
  }, [hasChanges]);
  const blocker = useBlocker({
    shouldBlockFn: shouldBlockNavigation,
    enableBeforeUnload: hasChanges,
    disabled: !hasChanges,
    withResolver: true,
  });
  const handleSave = async (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[]; rowOrigins: Array<number | null> }
  ) => {
    if (!selectedTable) return;
    const editBase = editBaseRef.current;
    if (!editBase || editBase.tableId !== String(selectedTable._id)) {
      throw new Error("Table rows are not ready to save");
    }
    // Keep each row's source page, so jobs on selected pages keep their rows.
    const previousRowPageNumbers = rowPayload?.rowPageNumbers;
    const rowPageNumbers =
      previousRowPageNumbers && metadata
        ? rowPagesAfterEdit(previousRowPageNumbers, metadata.rowOrigins)
        : undefined;

    let uploadedRows: Awaited<ReturnType<typeof uploadTableRowsPayload>> | null = null;
    try {
      uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: `project-documents/schedule-rows/${selectedTable._id}`,
            filename: `${selectedTable._id}-rows.json.gz`,
          }),
        headers,
        rows,
        rowPageNumbers,
      });

      const result = await updateMasterTableData({
        tableId: selectedTable._id,
        headers,
        rowsStorageKey: uploadedRows.rowsStorageKey,
        rowsFormat: uploadedRows.rowsFormat,
        rowCount: uploadedRows.rowCount,
        deletedRowIndices: metadata?.deletedRowIndices ?? [],
        ...(rowPageNumbers && { previousRowPageNumbers, rowPageNumbers }),
        expectedRowsVersion: editBase.rowsVersion,
      });
      setRowPayload({
        headers: [...headers],
        rows: rows.map((row) => [...row]),
        rowPageNumbers,
      });
      lastHydratedRowsPayloadKey.current = rowsPayloadKey;
      editBaseRef.current = {
        tableId: String(selectedTable._id),
        rowsVersion: result.rowsVersion,
      };
      toast.success("Master table saved. Changes apply everywhere this schedule is used.");
    } catch (error) {
      if (uploadedRows) {
        try {
          await discardUncommittedRowsPayload({
            tableId: selectedTable._id,
            rowsStorageKey: uploadedRows.rowsStorageKey,
          });
        } catch (cleanupError) {
          console.error("Failed to discard uncommitted schedule rows payload:", cleanupError);
        }
      }
      const message = error instanceof Error ? error.message : "";
      if (message.includes("modified by someone else")) {
        notifyError(error, "This table was modified by someone else. Reload to get the latest data.");
      } else {
        notifyError(error, "Failed to save table");
      }
      console.error("Error saving master schedule table:", error);
      throw error;
    }
  };

  const switchTable = (tableId: string) => {
    if (tableId === String(selectedTable?._id)) return;
    if (hasChanges) {
      setPendingTableId(tableId);
      return;
    }
    setSelectedTableId(tableId);
  };

  const confirmPendingSwitch = () => {
    if (!pendingTableId) return;
    setSelectedTableId(pendingTableId);
    setPendingTableId(null);
  };

  const navigateAfterConfirmation = (options: AppNavigateOptions) => {
    allowNextRouterNavigationRef.current = true;
    setPendingNavigation(null);
    appNavigate(options);
  };

  const completePendingAction = () => {
    if (pendingTableId) {
      confirmPendingSwitch();
      return;
    }
    if (pendingNavigation) {
      navigateAfterConfirmation(pendingNavigation);
      return;
    }
    if (blocker.status === "blocked") {
      blocker.proceed();
    }
  };

  const cancelPendingAction = () => {
    setPendingTableId(null);
    setPendingNavigation(null);
    if (blocker.status === "blocked") {
      blocker.reset();
    }
  };

  const requestNavigation = (options: AppNavigateOptions) => {
    if (hasChanges) {
      setPendingNavigation(options);
      return;
    }
    appNavigate(options);
  };

  const projectDocumentsNavigation: AppNavigateOptions = {
    to: "/project/documents",
    title: "Project Documents",
  };
  const documentNavigation: AppNavigateOptions = {
    to: "/project/documents/$documentId",
    params: { documentId },
    search: { revisionId: String(revision._id) },
    title: documentName,
  };
  const trail: Crumb[] = [
    {
      label: "Project Documents",
      href: "/project/documents",
      onClick: (event) => {
        if (!hasChanges) return;
        event.preventDefault();
        setPendingNavigation(projectDocumentsNavigation);
      },
    },
    {
      label: documentName,
      href: "/project/documents/$documentId",
      params: { documentId },
      onClick: (event) => {
        // Modified clicks open the link natively; plain clicks keep the revision and the unsaved-changes guard.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        requestNavigation(documentNavigation);
      },
    },
  ];
  const revisionMeta = (
    <>
      <RevisionBadge number={revision.revisionNumber} />
      <span
        className="hidden max-w-[16ch] truncate text-sm text-muted-foreground sm:inline"
        title={revision.revisionName}
      >
        {revision.revisionName}
      </span>
    </>
  );

  const pageHeader = <PageHeader title={pageTitle} trail={trail} meta={revisionMeta} />;

  if (extractionStatus !== "completed") {
    return (
      <div className="space-y-4">
        {pageHeader}
        {extractionStatus === "failed" ? (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Schedule extraction failed</AlertTitle>
            <AlertDescription>
              {revision.sharedScheduleExtraction?.error ??
                "The schedule extraction for this revision failed, so there are no master tables to edit."}
            </AlertDescription>
          </Alert>
        ) : extractionStatus ? (
          <Card>
            <ProcessingSpinner message="Schedule extraction is still processing. Master tables will appear here once it completes." />
          </Card>
        ) : (
          <Alert>
            <AlertCircle className="size-4" />
            <AlertTitle>No schedule extraction</AlertTitle>
            <AlertDescription>
              This revision has no schedule extraction, so there are no master tables to edit.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  if (tables === undefined) {
    return (
      <div className="space-y-4">
        {pageHeader}
        <Card>
          <ProcessingSpinner message="Loading schedule tables..." />
        </Card>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="space-y-4">
        {pageHeader}
        <Alert>
          <AlertCircle className="size-4" />
          <AlertTitle>No tables extracted</AlertTitle>
          <AlertDescription>
            The schedule extraction completed but did not produce any tables.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={pageTitle}
        trail={trail}
        meta={revisionMeta}
        actions={
          <Select value={String(selectedTable?._id ?? "")} onValueChange={switchTable}>
            <SelectTrigger size="sm" className="w-[min(260px,70vw)]" aria-label="Select table">
              <SelectValue placeholder="Select a table" />
            </SelectTrigger>
            <SelectContent>
              {tables.map((table) => (
                <SelectItem key={String(table._id)} value={String(table._id)}>
                  {table.name || "Untitled Table"} — page {table.pageNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,42%)]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-base">
              {selectedTable?.name || "Untitled Table"}
            </CardTitle>
            <CardDescription>
              {canEdit
                ? "Click any cell to edit. Changes are saved when you click the Save button. Edits here update the master extraction used by SpecPilot, TakeoffPilot, and the rest of the platform."
                : "The master extraction used by SpecPilot, TakeoffPilot, and the rest of the platform."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rowPayloadError ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Unable to load rows</AlertTitle>
                <AlertDescription>{rowPayloadError}</AlertDescription>
              </Alert>
            ) : rowPayload ? (
              <EditableDataGrid
                ref={gridRef}
                headers={rowPayload.headers}
                rows={rowPayload.rows}
                readOnly={!canEdit}
                onSave={handleSave}
                onHasChangesChange={setHasChanges}
                onIsSavingChange={setIsSaving}
              />
            ) : (
              <ProcessingSpinner message="Loading table rows..." />
            )}
          </CardContent>
        </Card>

        <SpecPilotSourcePdfPanel
          sourcePdf={sourcePdf}
          sourceId={String(revision._id)}
          pageNumber={selectedTable?.pageNumber ?? 1}
          className="h-[70dvh] min-h-[480px] lg:h-auto lg:min-h-[560px]"
        />
      </div>

      <UnsavedChangesDialog
        open={
          pendingTableId !== null ||
          pendingNavigation !== null ||
          blocker.status === "blocked"
        }
        onOpenChange={(open) => {
          if (!open) cancelPendingAction();
        }}
        isSaving={isSaving}
        onSave={async () => {
          await gridRef.current?.save();
          completePendingAction();
        }}
        onDiscard={() => {
          gridRef.current?.cancel();
          completePendingAction();
        }}
      />
    </div>
  );
}
